import { useState, useEffect } from "react";
import { KeyRound, AlertTriangle } from "lucide-react";

export default function WhatsAppLink() {
  const [connected, setConnected] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [botStatus, setBotStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const [generatedCode, setGeneratedCode] = useState("");
  const [qrBase64, setQrBase64] = useState(null);
  const [showCode, setShowCode] = useState(false);

  const [inputNumber, setInputNumber] = useState("");
  const [loadingCode, setLoadingCode] = useState(false);
  const [canGetCode, setCanGetCode] = useState(true);
  const [isNewChannel, setIsNewChannel] = useState(false);

  const [selectedMethod, setSelectedMethod] = useState("code");

  const [apiMessage, setApiMessage] = useState("");
  const [apiError, setApiError] = useState(false);

  // --------------- FETCH STATUS ---------------
  useEffect(() => {
    const fetchChannelHealth = async () => {
      const token = localStorage.getItem("token");

      try {
        const res = await fetch(`https://zaichat.net/api/channel-health`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            auth: `Bearer ${token}`,
          },
        });

        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

        const data = await res.json();
        console.log(data.data.status.text)
        if (data.data.status.text === "AUTH") {
          setConnected(true);
          setBotStatus("Active");
          setPhoneNumber("+" + data.data.user.id);
        } else {
          setConnected(false);
          setBotStatus("Disconnected");
        }
      } catch (err) {
        setConnected(false);
        setBotStatus("Disconnected");
      } finally {
        setLoading(false);
      }
    };

    fetchChannelHealth();
  }, []);

  // --------------- AUTO REFRESH CODE EVERY 60 SECS ---------------
  useEffect(() => {
    if (!showCode) return;
    const interval = setInterval(() => handleGetCode(), 60000);
    return () => clearInterval(interval);
  }, [showCode]);

  // --------------- GET LINK CODE / QR ---------------
  const handleGetCode = async () => {
    if (!inputNumber.trim()) {
      showError("Please enter your phone number.");
      return;
    }

    if (!canGetCode) {
      showError("⚠️ Please wait 5 minutes before requesting a new code.");
      return;
    }

    const cleaned = inputNumber.replace("+", "").replace(/\s+/g, "");
    const token = localStorage.getItem("token");

    try {
      setLoadingCode(true);

      const res = await fetch(
        `https://zaichat.net/api/connect-number?phone_number=${cleaned}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            auth: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) throw new Error();

      const data = await res.data.json();
      console.log(data);

      if (data.channel_mode === "TRIAL") {
        setIsNewChannel(true);
        setCanGetCode(false);
        startCooldown();
        showSuccess("Channel created. Try again after 5 minutes.");
        return;
      }

      const code = data.link_code || data.code;
      const qr = data.qr_code_base64;

      setGeneratedCode(code || "");
      setQrBase64(qr || null);
      setShowCode(true);

      showSuccess("Login code generated!");
    } catch (err) {
      showError("Failed to get code. Try again later.");
    } finally {
      setLoadingCode(false);
    }
  };

  // --------------- 5 MIN COOLDOWN ---------------
  const startCooldown = () => {
    setCanGetCode(false);
    setTimeout(() => setCanGetCode(true), 5 * 60 * 1000);
  };

  const showError = (msg) => {
    setApiMessage(msg);
    setApiError(true);
  };

  const showSuccess = (msg) => {
    setApiMessage(msg);
    setApiError(false);
  };

  // --------------- PAGE LOADING ----------------
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-primary text-lg font-semibold">
        Loading WhatsApp connection...
      </div>
    );
  }

  // --------------- MAIN UI --------------------
  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-cream/60 py-10 px-6 md:px-10">
      {/* HEADER */}
      <div className="text-center mb-12">
        <h1 className="text-5xl md:text-6xl font-extrabold text-primary">
          WhatsApp Connection
        </h1>
        <p className="text-dark/70 mt-3 text-lg max-w-2xl mx-auto">
          Connect your WhatsApp account to enable the bot.
        </p>
      </div>


      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* LEFT SIDE - CONNECTION */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-accent/10">
          <div className="flex items-center justify-center mb-6">
            <AlertTriangle size={60} className="text-yellow-500" />
          </div>

          <h2 className="text-2xl font-bold text-primary mb-6 text-center">
            {connected ? "Connected" : "Connect WhatsApp"}
          </h2>

          <p className="text-dark/70 text-sm mb-8 text-center">
            {connected
              ? "Your WhatsApp account is connected."
              : "Connect WhatsApp to enable the bot."}
          </p>

          {/* Connected UI */}
          {connected ? (
            <div className="flex flex-col gap-4 bg-green-50 border-2 border-green-200 rounded-xl p-6">
              <p className="text-sm text-dark/60 font-semibold">
                WhatsApp Number: <span className="text-primary">{phoneNumber}</span>
              </p>
              <p className="text-sm text-dark/60 font-semibold">
                Bot Status: <span className="text-green-600">{botStatus}</span>
              </p>
            </div>
          ) : (
            <div>
              {!showCode ? (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-primary mb-2">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      placeholder="Enter your phone number e.g. +972594435028"
                      value={inputNumber}
                      onChange={(e) => setInputNumber(e.target.value)}
                      className="border-2 border-accent/20 rounded-xl p-3 w-full focus:outline-none focus:border-accent transition-all"
                    />
                  </div>

                  <button
                    onClick={handleGetCode}
                    disabled={loadingCode || !canGetCode}
                    className="flex items-center justify-center gap-2 bg-gradient-to-r from-accent to-orange-500 text-white font-semibold px-6 py-3 rounded-xl mx-auto transition hover:scale-105 disabled:opacity-70 disabled:hover:scale-100"
                  >
                    {loadingCode ? "Generating..." : (
                      <>
                        <KeyRound size={18} /> Get Code
                      </>
                    )}
                  </button>

                  {apiMessage && (
                    <div className={`mt-4 p-3 rounded-xl text-sm border ${
                      apiError 
                        ? "bg-red-50 text-red-700 border-red-300" 
                        : "bg-blue-50 text-blue-700 border-blue-300"
                    }`}>
                      {apiMessage}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* METHOD SELECTOR */}
                  <div className="flex justify-center mb-6">
                    <button
                      onClick={() => setSelectedMethod("code")}
                      className={`px-6 py-2 rounded-l-xl border-2 font-semibold transition ${
                        selectedMethod === "code"
                          ? "bg-primary text-white border-primary"
                          : "bg-gray-100 text-dark border-gray-300"
                      }`}
                    >
                      Code
                    </button>

                    <button
                      onClick={() => setSelectedMethod("qr")}
                      className={`px-6 py-2 rounded-r-xl border-2 font-semibold transition ${
                        selectedMethod === "qr"
                          ? "bg-primary text-white border-primary"
                          : "bg-gray-100 text-dark border-gray-300"
                      }`}
                    >
                      QR
                    </button>
                  </div>

                  {selectedMethod === "code" ? (
                    <div className="bg-accent/10 border-2 border-accent rounded-xl p-6 mb-6 text-center">
                      <p className="text-3xl font-bold text-accent tracking-widest">
                        {generatedCode}
                      </p>
                    </div>
                  ) : qrBase64 ? (
                    <div className="flex justify-center mb-6">
                      <img
                        src={`data:image/png;base64,${qrBase64}`}
                        alt="WhatsApp QR"
                        className="w-64 h-64 border-2 border-accent rounded-xl shadow-lg"
                      />
                    </div>
                  ) : (
                    <p className="text-red-600 text-sm mb-6 text-center bg-red-50 border border-red-200 rounded-xl p-4">
                      QR not available yet — try again later.
                    </p>
                  )}

                  <button
                    onClick={() => {
                      setConnected(true);
                      setPhoneNumber(inputNumber);
                      setBotStatus("Active");
                      setShowCode(false);
                    }}
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold px-8 py-3 rounded-xl transition hover:scale-105 w-full"
                  >
                    Connect
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* RIGHT SIDE - INSTRUCTIONS */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-accent/10">
          <h2 className="text-2xl font-bold text-primary mb-6">
            How this page works
          </h2>

          <p className="text-dark/70 text-sm mb-4 leading-relaxed">
            This page allows you to connect your WhatsApp number with the chatbot
            system. Once connected, the bot will be able to send and receive
            messages using your WhatsApp account.
          </p>

          <div className="bg-cream/30 border border-accent/10 rounded-xl p-6 mb-4">
            <h3 className="text-lg font-bold text-primary mb-3">Steps to Connect:</h3>
            <ul className="text-dark/60 text-sm space-y-3">
              <li className="flex items-start gap-2">
                <span className="bg-accent text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                <span>Enter your phone number and request a login code or QR.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-accent text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                <span>If a new channel is created, you may need to wait 5 minutes before trying again.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-accent text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                <span>You can switch between viewing the login code or the QR using the selector.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-accent text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">4</span>
                <span>Once you complete the connection on WhatsApp, press <strong>Connect</strong> to activate your bot.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-accent text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">5</span>
                <span>When connected, you will see your WhatsApp number and the bot status.</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-dark/70 text-sm leading-relaxed">
              <strong className="text-primary">Note:</strong> This setup is required so your bot can communicate with users on WhatsApp.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-20 text-center text-dark/60 text-sm">
        Powered by <span className="text-accent font-semibold">Zuccess AI</span>
      </div>
    </div>
  );
}